/**
 * Application Name: Sahajaseeds-WAS
 * Author: Zevcore Private Limited
 * Starting date: 15.12.2022
 * Developer Name: Anand Krishna
 * Last modified date: 15.12.2022
 * Last modified by: Anand Krishna
 */

import React from "react";
import { Route, Routes } from "react-router-dom"; // For import the react dom for navigation components
import { NotificationsProvider } from "@mantine/notifications"; // For show the notification import
import { ColorSchemeProvider, MantineProvider } from "@mantine/core"; //For import mantine required functions and theme
import { useLocalStorage } from "@mantine/hooks";

// Components
import ProtectedRoute from "./components/ProtectRoute";
import ErrorBoundary from "./components/Error";
import Sidebar from "./components/Sidebar";
import Top from "./pages/auth/Top";

// Authentications
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";

// Import pages
import Dashboard from "./pages/Home";
import Manages from "./pages/settings/Manage";
import Profile from "./pages/settings/Profile";
import Database from "./pages/settings/Database";
import Setting from "./pages/settings/Setting";
import Invoice from "./pages/settings/Invoice";
import Banking from "./pages/settings/Banking";
import Address from "./pages/settings/Address";

// Cash Ledger
import AddCash from "./pages/cash/AddCash";
import Bank from "./pages/cash/Bank";
import SearchCash from "./pages/cash/SearchCash";
import SearchBank from "./pages/cash/SearchBank";

// Products sections
import Category from "./pages/product/Category";
import Product from "./pages/product/Product";

// Inventory Section
import Vendor from "./pages/inventory/Vendor";
import StockIn from "./pages/inventory/StockIn";
import PurchaseOrder from "./pages/inventory/PurchaseOrder";
import PurchaseUnitOrder from "./pages/inventory/PurchaseUnitOrder";
import Stock from "./pages/inventory/Stock";
import UnitStockIn from "./pages/inventory/UnitStockIn";
import Tax from "./pages/settings/Tax";
import Hsn from "./pages/settings/Hsn";
import SaleType from "./pages/settings/SaleType";
import Account from "./pages/settings/Account";
import Customer from "./pages/Customer";
import ProductStock from "./pages/inventory/ProductStock";
import ProductPacketStock from "./pages/inventory/ProductPacketStock";

// Inventory Section
import AddSale from "./pages/sale/AddSale";
import Sale from "./pages/sale/Sale";
import SaveSale from "./pages/sale/SaveSale";
import ViewSave from "./pages/sale/ViewSave";
import PrintInvoice from "./pages/sale/PrintInvoice";
import HsnReport from "./pages/sale/HsnReport";
import PacketStock from "./pages/inventory/PacketStock";

// Reports
import WeeklySales from "./pages/reports/WeeklySales";
import MonthlySales from "./pages/reports/MonthlySales";
import GSTReport from "./pages/reports/GSTReport";

function App() {
  // For get the dark light toggle
  const toggleColorScheme = (value) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));
  // Get the last mode
  const [colorScheme, setColorScheme] = useLocalStorage({
    key: "mantine-color-scheme",
    defaultValue: "light",
  });
  // Change the color if u want to '#043c64' Carefully wile choosing dark color only
  const [color] = useLocalStorage({
    key: "color",
    defaultValue: "#043c64",
  });

  // For make color lighter by 10 colors level
  const LightenDarkenColor = (col, amt) => {
    var usePound = false;
    if (col[0] === "#") {
      col = col.slice(1);
      usePound = true;
    }
    var num = parseInt(col, 16);
    var r = (num >> 16) + amt;
    if (r > 255) r = 255;
    else if (r < 0) r = 0;
    var b = ((num >> 8) & 0x00ff) + amt;
    if (b > 255) b = 255;
    else if (b < 0) b = 0;
    var g = (num & 0x0000ff) + amt;
    if (g > 255) g = 255;
    else if (g < 0) g = 0;
    return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
  };
  return (
    <ColorSchemeProvider
      colorScheme={colorScheme}
      toggleColorScheme={toggleColorScheme}
    >
      {/* Mantine provider is used for customizing our own theme */}
      <MantineProvider
        theme={{
          "::-webkit-scrollbar": {
            backgroundColor: LightenDarkenColor(color, 120),
            width: "5px",
            height: "10px",
            borderRadius: 5,
            fontFamily: "Poppins",
          },
          "::-webkit-scrollbar-thumb": {
            backgroundColor: LightenDarkenColor(color, 120),
            borderRadius: 5,
            // "#D50000"
          },
          fontFamily: "Poppins",
          fontFamilyMonospace: "Poppins",
          headings: { fontFamily: "Poppins" },
          fontWeight: 200,
          colorScheme,
          colors: {
            zevcore: [
              LightenDarkenColor(color, 140),
              LightenDarkenColor(color, 130),
              LightenDarkenColor(color, 120),
              LightenDarkenColor(color, 110),
              LightenDarkenColor(color, 100),
              LightenDarkenColor(color, 90),
              LightenDarkenColor(color, 50),
              LightenDarkenColor(color, 80),
              LightenDarkenColor(color, 50),
              LightenDarkenColor(color, 20),
            ],
          },
        }}
      >
        {/* Mantine notifications */}
        <NotificationsProvider autoClose={3000}>
          <ErrorBoundary>
            <Routes>
              {/* For authentications */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot_password" element={<ForgotPassword />} />
              <Route element={<Sidebar />}>
                <Route
                  exact
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/home/:id"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/cash"
                  element={
                    <ProtectedRoute>
                      <AddCash />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/bank_cash"
                  element={
                    <ProtectedRoute>
                      <Bank />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/search_cash"
                  element={
                    <ProtectedRoute>
                      <SearchCash />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/search_bank"
                  element={
                    <ProtectedRoute>
                      <SearchBank />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/category"
                  element={
                    <ProtectedRoute>
                      <Category />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/products"
                  element={
                    <ProtectedRoute>
                      <Product />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/vendors"
                  element={
                    <ProtectedRoute>
                      <Vendor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/stock_in"
                  element={
                    <ProtectedRoute>
                      <StockIn />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/unitstock_in"
                  element={
                    <ProtectedRoute>
                      <UnitStockIn />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/product_stock"
                  element={
                    <ProtectedRoute>
                      <ProductStock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/product_packet_stock"
                  element={
                    <ProtectedRoute>
                      <ProductPacketStock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/po"
                  element={
                    <ProtectedRoute>
                      <PurchaseOrder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/unit_po"
                  element={
                    <ProtectedRoute>
                      <PurchaseUnitOrder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/stock"
                  element={
                    <ProtectedRoute>
                      <Stock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/packetStock"
                  element={
                    <ProtectedRoute>
                      <PacketStock />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/manages"
                  element={
                    <ProtectedRoute>
                      <Manages />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/database"
                  element={
                    <ProtectedRoute>
                      <Database />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Setting />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/tax"
                  element={
                    <ProtectedRoute>
                      <Tax />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/hsn"
                  element={
                    <ProtectedRoute>
                      <Hsn />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/sale_type"
                  element={
                    <ProtectedRoute>
                      <SaleType />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <Customer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/add_sale"
                  element={
                    <ProtectedRoute>
                      <AddSale />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/sale"
                  element={
                    <ProtectedRoute>
                      <Sale />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/weekly_sales"
                  element={
                    <ProtectedRoute>
                      <WeeklySales />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/monthly_sales"
                  element={
                    <ProtectedRoute>
                      <MonthlySales />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/gst_report"
                  element={
                    <ProtectedRoute>
                      <GSTReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/view_save"
                  element={
                    <ProtectedRoute>
                      <ViewSave />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/add_save/:id"
                  element={
                    <ProtectedRoute>
                      <SaveSale />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/hsn"
                  element={
                    <ProtectedRoute>
                      <HsnReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/address"
                  element={
                    <ProtectedRoute>
                      <Address />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/banking"
                  element={
                    <ProtectedRoute>
                      <Banking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/invoice"
                  element={
                    <ProtectedRoute>
                      <Invoice />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                exact
                path="/print_invoice/:id"
                element={
                  <ProtectedRoute>
                    <PrintInvoice />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ErrorBoundary>
        </NotificationsProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

export default App;
